import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { StudentRecordResponse } from "@mubin/contracts";
import { api } from "../lib/api";
import { Badge, Empty, Loading } from "./ui";

const decisionLabel = { passed: "ناجح", repeat: "إعادة", partial: "جزئي" } as const;
const decisionTone = { passed: "success", repeat: "warning", partial: "info" } as const;
const kindLabel: Record<string, string> = {
  new: "حفظ جديد",
  near_review: "مراجعة قريبة",
  far_review: "مراجعة بعيدة",
  repeat: "إعادة",
};

const iso = (d: Date) => d.toISOString().slice(0, 10);
const daysAgo = (n: number) => iso(new Date(Date.now() - n * 86_400_000));
const PERIODS = { 7: "آخر أسبوع", 30: "آخر 30 يومًا", 90: "آخر 3 أشهر" } as const;

/** The student's record over a period: plan progress, attendance and results, from real data only. */
export function StudentRecord({ studentId }: { studentId: string }) {
  const [period, setPeriod] = useState<keyof typeof PERIODS>(30);
  const from = daysAgo(period - 1);
  const to = iso(new Date());
  const rec = useQuery({
    queryKey: ["student-record", studentId, from, to],
    queryFn: () => api<StudentRecordResponse>(`/v1/students/${studentId}/record?from=${from}&to=${to}`),
  });
  const r = rec.data;

  return (
    <section className="card">
      <div className="card-h">
        <h2>سجل الطالب</h2>
        <select
          aria-label="الفترة"
          value={period}
          onChange={(e) => setPeriod(Number(e.target.value) as keyof typeof PERIODS)}
        >
          {Object.entries(PERIODS).map(([n, label]) => (
            <option key={n} value={n}>
              {label}
            </option>
          ))}
        </select>
      </div>
      {rec.isPending ? (
        <Loading rows={3} />
      ) : !r ? (
        <Empty title="تعذر تحميل السجل" />
      ) : (
        <div className="section record">
          <h3>التقدم في الخطة</h3>
          {r.plans.length === 0 ? (
            <p className="muted">لا خطة معتمدة.</p>
          ) : (
            r.plans.map((p) => {
              const pct = p.totalAyahs ? Math.round((p.doneAyahs / p.totalAyahs) * 100) : 0;
              return (
                <div key={p.planId} className="record-plan">
                  <div className="record-row">
                    <span>
                      {p.circle.name} · من {p.start} إلى {p.end}
                    </span>
                    {p.provisional && <Badge tone="warning">أولية</Badge>}
                  </div>
                  <div
                    className="progress"
                    role="progressbar"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="نسبة إنجاز المقرر"
                  >
                    <div style={{ width: `${pct}%` }} />
                  </div>
                  <p className="muted small">
                    أتمّ {p.doneAyahs} من {p.totalAyahs} آية ({pct}٪) منذ {p.startedOn}. البنود المستحقة حتى
                    اليوم: منجز {p.due.passed}
                    {p.due.partial ? `، منجز جزئيًا ${p.due.partial}` : ""}، إعادة {p.due.repeat}، لم يُسمَّع{" "}
                    {p.due.pending}.
                  </p>
                </div>
              );
            })
          )}

          <h3>الحضور</h3>
          {r.attendance.recordedDays === 0 ? (
            <p className="muted">لا حضور مسجل في هذه الفترة.</p>
          ) : (
            <>
              <p>
                حاضر {r.attendance.presentDays} من {r.attendance.recordedDays} يومًا مسجلًا (
                {Math.round((r.attendance.presentDays / r.attendance.recordedDays) * 100)}٪)
              </p>
              <div className="record-chips">
                {r.attendance.counts.map((c) => (
                  <Badge key={c.statusId} tone="neutral">
                    {r.attendance.statuses.find((s) => s.id === c.statusId)?.name ?? "—"}: {c.count}
                  </Badge>
                ))}
              </div>
            </>
          )}

          <h3>نتائج التسميع</h3>
          {r.results.length === 0 ? (
            <p className="muted">لا نتائج في هذه الفترة.</p>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>التاريخ</th>
                    <th>النوع</th>
                    <th>المقطع</th>
                    <th>القرار</th>
                    <th>المعلم</th>
                  </tr>
                </thead>
                <tbody>
                  {r.results.map((x) => (
                    <tr key={x.id}>
                      <td className="num small">{x.recordedAt.slice(0, 10)}</td>
                      <td className="small">{x.kind ? (kindLabel[x.kind] ?? x.kind) : "—"}</td>
                      <td className="small">{x.range ?? "—"}</td>
                      <td>
                        <Badge tone={decisionTone[x.decision]}>{decisionLabel[x.decision]}</Badge>
                        {x.revision > 1 && <span className="muted small"> (معدّلة)</span>}
                        {x.errors > 0 && <span className="muted small"> · {x.errors} ملاحظة</span>}
                      </td>
                      <td className="small">{x.teacher}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
